import { api } from 'api';
import { locationsApi } from 'services/locationsApi';

import { AppThunk } from 'store';
import {
  selectLocation,
  setError,
  setIsAuthorized,
  setIsLoading,
  setUser
} from 'store/reducers/userSlice';

import { ContentFilter, UpdateClDetailsParams } from 'types';

export const getClientDetails = (): AppThunk => async (dispatch, getState) => {
  try {
    const { data } = await api.client.getClDetails();
    const {
      locations: { locations },
      locationsApi: { queries },
      user: { selectedLocation }
    } = getState();
    let locationList = locations ?? queries?.['getLocations(1)']?.data ?? [];

    if (locationList.length === 0) {
      const locationsResult = await dispatch(
        locationsApi.endpoints.getLocations.initiate(ContentFilter.ServicesOnly)
      );
      if (locationsResult.data) {
        locationList = locationsResult.data;
      }
    }

    if (selectedLocation === null) {
      const homeLocations = locationList.find(
        (storeLocation) => storeLocation.id === data[0]?.home_store_id
      );
      const firstAvailableLocation =
        locationList.length > 0 ? locationList[0] : null;

      dispatch(selectLocation(homeLocations ?? firstAvailableLocation));
    }

    dispatch(setUser(data[0]));
    dispatch(setIsAuthorized(true));
  } catch (error) {
    console.error(`Error while get client details: ${error.message}`);

    if (error?.response?.status === 403) return;

    dispatch(setError(error.response?.data?.error || error.message));
  }
};

export const updateClDetails =
  (params: UpdateClDetailsParams): AppThunk<Promise<boolean | null>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.client.updateClDetails(params);
      if (data.success) {
        await dispatch(getClientDetails());
        return data.success;
      }
    } catch (error) {
      console.error(`Error updating client information: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const updatePassword =
  (newPass: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.client.updatePass(newPass);
      return response.data?.success;
    } catch (error) {
      console.error(`Error updating client password: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const resetPassword =
  ({
    password,
    clid,
    token
  }: {
    password: string;
    clid: string | number;
    token: number | null;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.client.updatePass(password, {
        clid,
        token
      });
      return response.data;
    } catch (error) {
      console.error(`Error set client password: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const updateClPhoto =
  (file: File): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.client.uploadClPhoto(file);

      return data.success;
    } catch (error) {
      console.error(`Error updating client photo: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
